import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { ToastrService } from 'ngx-toastr';
import { inject } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { Router } from '@angular/router';
import { PAGE_ROUTES } from '@shared/core-data';
import { AuthService } from '@shared/auth';
import { EXTERNAL_REQUEST, SKIP_ERROR_TOAST } from '@core-utils';

export const AppInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(EXTERNAL_REQUEST)) {
    return next(req);
  }

  const BASE_URL = environment.api;
  const authService = inject(AuthService);
  const toastrService = inject(ToastrService);
  const router = inject(Router);

  const isProtectedEndpoint = [
    environment.keycloak.oauthServerHost,
    'assets',
  ].some((url) => req.url.includes(url));

  if (!isProtectedEndpoint) {
    const headers: { [name: string]: string } = {};

    const token = authService.accessToken;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (req.method === 'POST') {
      headers['Idempotency-Key'] = uuidv4();
    }

    const authReq = req.clone({
      setHeaders: headers,
      url: `${BASE_URL}${req.url}`,
    });

    return next(authReq).pipe(
      catchError(({ error, status }: HttpErrorResponse) => {
        if (status === 401) {
          router.navigate(['/' + PAGE_ROUTES.LOADING]).then(() => {
            authService.initLoginFlow();
          });
        }

        const skipErrorToast = req.context.get(SKIP_ERROR_TOAST);
        if (!skipErrorToast) {
          let errorMessage =
            'An unexpected error occurred. Please try again later.';

          let parsedError = error;
          if (typeof error === 'string') {
            parsedError = JSON.parse(error);
          }

          if (parsedError?.detail) {
            errorMessage = parsedError.detail;
          }

          if (parsedError?.errors?.length > 0) {
            errorMessage = parsedError.errors[0].description;
          }

          toastrService.error(errorMessage, 'Error');
        }

        return throwError(() => error);
      }),
    );
  }

  return next(req);
};
